/**
 * Bestiary Types
 *
 * Shared types for the creature template library (machina_bestiary)
 * and live encounter mob instances (machina_encounter_mobs).
 */
import { z } from 'zod';
// =============================================================================
// BESTIARY ENTRY (creature template)
// =============================================================================
export const AbilitiesSchema = z.object({
    str: z.number().default(10),
    dex: z.number().default(10),
    con: z.number().default(10),
    int: z.number().default(10),
    wis: z.number().default(10),
    cha: z.number().default(10),
});
export const SavesSchema = z.record(z.string(), z.number()).default({});
export const BestiaryActionSchema = z.object({
    name: z.string(),
    attackBonus: z.number(),
    damageDice: z.string(),
    damageType: z.string(),
    range: z.enum(['melee', 'ranged']),
    description: z.string().optional(),
});
export const BestiaryTacticsSchema = z.object({
    iq: z.string().optional(),
    mistakeChance: z.number().optional(),
    targeting: z.string().optional(),
    style: z.string().optional(),
    positioning: z.string().optional(),
}).passthrough().default({});
export const BestiaryEntrySchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    slug: z.string().nullable().optional(),
    name: z.string(),
    cr: z.string().nullable().optional(),
    size: z.string().default('medium'),
    creatureType: z.string().nullable().optional(),
    alignment: z.string().nullable().optional(),
    ac: z.number(),
    maxHp: z.number(),
    abilities: AbilitiesSchema,
    saves: SavesSchema,
    actions: z.array(BestiaryActionSchema).default([]),
    tactics: BestiaryTacticsSchema,
    speed: z.number().default(30),
    resistances: z.array(z.string()).default([]),
    immunities: z.array(z.string()).default([]),
    vulnerabilities: z.array(z.string()).default([]),
    conditionImmunities: z.array(z.string()).default([]),
    imgUrl: z.string().nullable().optional(),
    /** Persisted HD-2D iso sprites (4 facings → Storage URLs). Copied to mob instance on spawn. */
    isoSprites: z.object({
        south: z.string().optional(),
        north: z.string().optional(),
        east: z.string().optional(),
        west: z.string().optional(),
    }).nullable().optional(),
    notes: z.string().nullable().optional(),
    isNamed: z.boolean().default(false),
    tags: z.array(z.string()).default([]),
    source: z.string().default('custom'),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// =============================================================================
// ENCOUNTER MOB (live combat instance)
// =============================================================================
export const EncounterMobSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    bestiaryId: z.string().uuid().nullable().optional(),
    displayName: z.string(),
    currentHp: z.number(),
    maxHp: z.number(),
    tempHp: z.number().default(0),
    ac: z.number(),
    dead: z.boolean().default(false),
    conditions: z.array(z.object({
        type: z.string(),
        sourceId: z.string().optional(),
        duration: z.number().optional(),
    })).default([]),
    attention: z.record(z.string(), z.any()).default({}),
    abilities: AbilitiesSchema,
    saves: SavesSchema,
    actions: z.array(BestiaryActionSchema).default([]),
    tactics: BestiaryTacticsSchema,
    speed: z.number().default(30),
    resistances: z.array(z.string()).default([]),
    immunities: z.array(z.string()).default([]),
    vulnerabilities: z.array(z.string()).default([]),
    conditionImmunities: z.array(z.string()).default([]),
    imgUrl: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    isNamed: z.boolean().default(false),
    cr: z.string().nullable().optional(),
    size: z.string().default('medium'),
    creatureType: z.string().nullable().optional(),
    baseName: z.string().nullable().optional(),
    open5eSlug: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=bestiary-types.js.map